// ---------------------------------------------------------------------------
// FeltonFunnel -- Funnel Builder Page (V11)
// ---------------------------------------------------------------------------
// Progressive funnel visualization for building audience personas.
// Uses local state + React Query (consistent with Discovery pattern).
// ---------------------------------------------------------------------------

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Switch } from '@/components/ui/switch';
import {
  Filter,
  FolderOpen,
  Scale,
  Plus,
  X,
  FileText,
  Users,
  Save,
  Download,
  ArrowDown,
  Loader2,
  Compass,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { useQuery } from '@tanstack/react-query';
import { useV11Layout } from '@/contexts/V11LayoutContext';
import { GlobalTimeBar } from '@/components/v11/discovery';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { DimensionPickerModal } from '@/components/v11/funnel/modals/DimensionPickerModal';
import { ValuePickerModal } from '@/components/v11/funnel/modals/ValuePickerModal';
import { SavePersonaModal } from '@/components/v11/funnel/modals/SavePersonaModal';
import { LoadPersonaModal } from '@/components/v11/funnel/modals/LoadPersonaModal';
import type { ValueOption } from '@/components/v11/funnel/modals/ValuePickerModal';
import type { DimensionConfig } from '@/components/v11/funnel/constants';
import {
  DIMENSION_FILTER_OPTIONS_MAP,
  GENERATION_OPTIONS,
} from '@/components/v11/funnel/constants';
import { FOLLOWER_TIERS } from '@/components/v11/discovery/constants';
import type { FunnelLayer, FunnelLayerResult, UnitType } from '@/components/v11/funnel/types';
import { getFilterOptions } from '@/services/v11/filterOptionsService';
import type { FilterOptions } from '@/services/v11/filterOptionsService';
import { useFunnelCalculation } from '@/hooks/useFunnelCalculation';
import { useSavePersona } from '@/hooks/useSavePersona';
import { useLoadPersona } from '@/hooks/useLoadPersona';
import { funnelService } from '@/services/v11/funnelService';
import type { ValueCountsData } from '@/services/v11/funnelService';
import type { Persona } from '@/services/v11/personaService';
import { useToast } from '@/hooks/use-toast';
import { getPlatformIcon } from '@/components/v11/shared/platformIcons';
// Dimensions that use search-as-you-type instead of a static list
const SEARCH_DIMENSIONS = new Set(['brand', 'team', 'item', 'channel', 'post', 'hashtag']);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateId(): string {
  return crypto.randomUUID();
}

/** Dimensions where the DB column stores string values, not numeric IDs */
const STRING_VALUE_DIMENSIONS = new Set(['emotion']);

/** Lookup map for follower tier ranges (includes "medio" alias for "mid" since API returns "Medio") */
const FOLLOWER_TIER_RANGES = new Map([
  ...FOLLOWER_TIERS.map(tier => [tier.label.toLowerCase(), tier.range] as const),
  ['medio', '50K-500K'], // API returns "Medio" instead of "Mid"
]);

/** Resolve ValueOption[] for a given dimension from filter options or local constants */
function getValuesForDimension(
  dimensionKey: string,
  filterOptions: FilterOptions | undefined
): readonly ValueOption[] {
  // Check API-sourced dimensions first
  const filterKey = DIMENSION_FILTER_OPTIONS_MAP[dimensionKey];
  if (filterKey && filterOptions) {
    const options = filterOptions[filterKey];
    if (Array.isArray(options)) {
      const useNameAsValue = STRING_VALUE_DIMENSIONS.has(dimensionKey);
      return options.map(opt => {
        let label = opt.name;
        // Add range to follower tier labels
        if (dimensionKey === 'followerTier') {
          label = `${opt.name} (${FOLLOWER_TIER_RANGES.get(opt.name.toLowerCase()) ?? ''})`;
        }
        // Capitalize gender labels (API returns lowercase)
        else if (dimensionKey === 'gender') {
          label = opt.name.charAt(0).toUpperCase() + opt.name.slice(1);
        }
        return {
          value: useNameAsValue ? opt.name : opt.id,
          label,
        };
      });
    }
  }

  // Age → Generations (groups of age_interval IDs)
  if (dimensionKey === 'age') {
    return GENERATION_OPTIONS.map(gen => ({
      value: gen.key,
      label: gen.label,
    }));
  }

  // Funnel-specific options with correct numeric DB IDs (media_type_id, category_id)
  if (dimensionKey === 'mediaType') {
    return [
      { value: 1, label: 'Photo' },
      { value: 2, label: 'Carousel' },
      { value: 3, label: 'Reel' },
      { value: 4, label: 'Animated Gif' },
      { value: 5, label: 'Text' },
      { value: 6, label: 'Slideshow' },
    ];
  }
  if (dimensionKey === 'category') {
    return [
      { value: 1, label: 'Game Highlights & Player Performances' },
      { value: 2, label: 'Breaking News & Announcements' },
      { value: 3, label: 'Entertainment & Celebrity Appearances' },
      { value: 4, label: 'Throwbacks & Historical Moments' },
      { value: 5, label: 'Fan Engagement & Memes' },
      { value: 6, label: 'Stat Updates & League Standings' },
      { value: 7, label: 'Merchandise & Promotions' },
      { value: 8, label: 'Inspirational & Motivational Posts' },
      { value: 9, label: 'Event & Game Promotions' },
    ];
  }
  if (dimensionKey === 'verified') {
    return [
      { value: 1, label: 'Verified' },
      { value: 0, label: 'Not Verified' },
    ];
  }
  if (dimensionKey === 'famous') {
    return [
      { value: 1, label: 'Famous' },
      { value: 0, label: 'Not Famous' },
    ];
  }
  if (dimensionKey === 'isPerson') {
    return [
      { value: 1, label: 'Person' },
      { value: 0, label: 'Organization / Not a Person' },
    ];
  }
  if (dimensionKey === 'toxic') {
    return [
      { value: 1, label: 'Toxic' },
      { value: 0, label: 'Not Toxic' },
    ];
  }
  if (dimensionKey === 'spam') {
    return [
      { value: 1, label: 'Spam' },
      { value: 0, label: 'Not Spam' },
    ];
  }
  if (dimensionKey === 'sentiment') {
    return [
      { value: 'positive', label: 'Positive' },
      { value: 'neutral', label: 'Neutral' },
      { value: 'negative', label: 'Negative' },
    ];
  }
  if (dimensionKey === 'paidOrganic') {
    return [
      { value: 'paid', label: 'Paid' },
      { value: 'organic', label: 'Organic' },
    ];
  }

  return [];
}

/** Merge layer definitions with calculation results */
function mergeLayersWithResults(
  layers: readonly FunnelLayer[],
  results: readonly FunnelLayerResult[] | undefined
): readonly FunnelLayerResult[] {
  if (!results || results.length === 0) return [];

  return layers.map((layer, index) => {
    const result = results[index];
    if (!result)
      return {
        ...layer,
        count: 0,
        previousCount: 0,
        reductionPercent: 0,
        unit: 'profiles' as const,
      };
    return {
      ...layer,
      count: result.count,
      previousCount: result.previousCount,
      reductionPercent: result.reductionPercent,
      unit: result.unit,
    };
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const FeltonFunnel: React.FC = () => {
  const { setHeaderControls } = useV11Layout();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<string>('12M');
  const [compareMode, setCompareMode] = useState(false);

  // Include Own Channels toggle (URL-persisted: ioc=1 means show own channels)
  const includeOwnChannels = searchParams.get('ioc') === '1';
  const setIncludeOwnChannels = useCallback((value: boolean) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set('ioc', '1');
      } else {
        next.delete('ioc');
      }
      return next;
    });
  }, [setSearchParams]);

  // Layer state
  const [layers, setLayers] = useState<readonly FunnelLayer[]>([]);
  const [layersB, setLayersB] = useState<readonly FunnelLayer[]>([]);

  // Which funnel is being edited ('A' or 'B')
  const [activeFunnel, setActiveFunnel] = useState<'A' | 'B'>('A');

  // Modal state
  const [isDimensionModalOpen, setIsDimensionModalOpen] = useState(false);
  const [isValueModalOpen, setIsValueModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);
  const [selectedDimension, setSelectedDimension] = useState<DimensionConfig | null>(null);

  // Brand/team search state (for search-mode dimensions)
  const [searchResults, setSearchResults] = useState<readonly ValueOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Value counts (per-value counts for the value picker modal)
  const [valueCounts, setValueCounts] = useState<ValueCountsData | undefined>(undefined);
  const [isLoadingValueCounts, setIsLoadingValueCounts] = useState(false);

  // Filter options (cached, shared across modals)
  const { data: filterOptions, isLoading: isLoadingOptions } = useQuery({
    queryKey: ['v11-filter-options'],
    queryFn: getFilterOptions,
    staleTime: 5 * 60 * 1000,
  });

  // Pre-load origin count on page mount (cached in Redis + React Query)
  const { data: preloadedOrigin, isLoading: isLoadingOrigin } = useQuery({
    queryKey: ['funnel-origin-count', includeOwnChannels],
    queryFn: () => funnelService.getOriginCount(includeOwnChannels),
    staleTime: 5 * 60 * 1000,
  });

  // Funnel calculations
  const funnelA = useFunnelCalculation(includeOwnChannels);
  const funnelB = useFunnelCalculation(includeOwnChannels);

  // Save persona
  const { savePersona, isSaving } = useSavePersona();

  // Load persona
  const {
    personas,
    isLoading: isLoadingPersonas,
    refetch: refetchPersonas,
    deletePersona,
    isDeleting,
  } = useLoadPersona();

  // Trigger calculation when layers change
  // NOTE: calculate() internally detects incremental changes (only last layer added)
  // and preserves previous results to avoid re-rendering the entire funnel
  useEffect(() => {
    funnelA.calculate(layers);
  }, [layers]);

  useEffect(() => {
    if (compareMode) {
      funnelB.calculate(layersB);
    }
  }, [layersB, compareMode]);

  // Inject GlobalTimeBar into header (hidden for now)
  // useEffect(() => {
  //   setHeaderControls(
  //     <GlobalTimeBar selectedPeriod={period as any} onPeriodChange={setPeriod as any} />
  //   );
  //   return () => setHeaderControls(null);
  // }, [period, setPeriod, setHeaderControls]);

  // Is the current dimension a search-mode dimension?
  const isSearchDimension = selectedDimension
    ? SEARCH_DIMENSIONS.has(selectedDimension.key)
    : false;

  // Resolve values for the currently selected dimension
  const dimensionValues = useMemo<readonly ValueOption[]>(() => {
    if (!selectedDimension) return [];
    if (isSearchDimension) return searchResults;
    return getValuesForDimension(selectedDimension.key, filterOptions);
  }, [selectedDimension, filterOptions, isSearchDimension, searchResults]);

  // Search handler for search-mode dimensions (brand, team, channel, post)
  const handleSearch = useCallback(
    async (query: string) => {
      if (!selectedDimension) return;
      setIsSearching(true);
      try {
        const key = selectedDimension.key;
        if (key === 'hashtag') {
          const results = await funnelService.searchHashtags(query);
          setSearchResults(
            results.map(r => ({
              value: r.id,
              label: `${r.name} (${r.postsCount.toLocaleString()} posts)`,
            }))
          );
        } else if (key === 'channel') {
          const results = await funnelService.searchChannels(query);
          setSearchResults(
            results.map(r => ({
              value: r.id,
              label: r.name.toLowerCase(),
              platform: r.platform,
              count: r.postsCount,
            }))
          );
        } else if (key === 'post') {
          const results = await funnelService.searchPosts(query);
          setSearchResults(
            results.map(r => ({
              value: r.id,
              label: `${r.caption} — @${r.author}`,
            }))
          );
        } else if (key === 'item') {
          const results = await funnelService.searchItems(query);
          setSearchResults(results.map(r => ({ value: r.id, label: r.name })));
        } else if (key === 'team') {
          const results = await funnelService.searchTeams(query);
          setSearchResults(results.map(r => ({
            value: r.id,
            label: r.name,
            badge: (r as any).type === 'player'
              ? { text: 'Player', color: '#f59e0b' }
              : { text: 'Team', color: '#10b981' },
          })));
        } else {
          // brand
          const results = await funnelService.searchBrands(query);
          setSearchResults(results.map(r => ({ value: r.id, label: r.name })));
        }
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [selectedDimension]
  );

  // Track which layer ID was just added (for animation control)
  const lastAddedLayerIdRef = useRef<string | null>(null);

  // Merged layers with results
  const layersWithResults = useMemo(
    () => mergeLayersWithResults(layers, funnelA.results?.layers as unknown as FunnelLayerResult[]),
    [layers, funnelA.results]
  );

  const layersBWithResults = useMemo(
    () =>
      mergeLayersWithResults(layersB, funnelB.results?.layers as unknown as FunnelLayerResult[]),
    [layersB, funnelB.results]
  );

  // Handlers
  const handleAddFilter = useCallback((funnel: 'A' | 'B') => {
    setActiveFunnel(funnel);
    setIsDimensionModalOpen(true);
  }, []);

  // Callback for DimensionPickerModal to fetch counts on demand (toggle)
  const handleRequestDimensionCounts = useCallback(async () => {
    const currentLayers = activeFunnel === 'A' ? layers : layersB;
    const audienceLayers = currentLayers
      .filter(l => l.type === 'audience')
      .map(l => ({ type: l.type, dimension: l.dimension, values: [...l.values] }));

    return funnelService.getDimensionCounts(audienceLayers, includeOwnChannels);
  }, [activeFunnel, layers, layersB, includeOwnChannels]);

  const handleSelectDimension = useCallback(
    (dimension: DimensionConfig) => {
      setSelectedDimension(dimension);
      setSearchResults([]);
      setValueCounts(undefined);
      setIsDimensionModalOpen(false);
      setIsValueModalOpen(true);

      // Auto-load channels when Channel dimension is selected (UX improvement)
      if (dimension.key === 'channel') {
        setIsSearching(true);
        funnelService
          .searchChannels('') // Empty query = list all account channels
          .then(results => {
            setSearchResults(
              results.map(r => ({
                value: r.id,
                label: r.name.toLowerCase(),
                platform: r.platform,
                count: r.postsCount,
              }))
            );
          })
          .catch(() => setSearchResults([]))
          .finally(() => setIsSearching(false));
      }
      // Auto-load posts when Post dimension is selected
      else if (dimension.key === 'post') {
        setIsSearching(true);
        funnelService
          .searchPosts('') // Empty query = list top posts
          .then(results => {
            setSearchResults(
              results.map(r => ({
                value: r.id,
                label: `${r.caption} — @${r.author}`,
              }))
            );
          })
          .catch(() => setSearchResults([]))
          .finally(() => setIsSearching(false));
      }
      // Fetch per-value counts (skip for other search-mode dimensions like brand/team/hashtag)
      else if (!SEARCH_DIMENSIONS.has(dimension.key)) {
        const currentLayers = activeFunnel === 'A' ? layers : layersB;
        const audienceLayers = currentLayers
          .filter(l => l.type === 'audience')
          .map(l => ({ type: l.type, dimension: l.dimension, values: [...l.values] }));

        setIsLoadingValueCounts(true);
        funnelService
          .getValueCounts(audienceLayers, dimension.type, dimension.key, includeOwnChannels)
          .then(data => setValueCounts(data))
          .catch(() => setValueCounts(undefined))
          .finally(() => setIsLoadingValueCounts(false));
      }
    },
    [activeFunnel, layers, layersB]
  );

  const handleApplyValues = useCallback(
    (selectedValues: (string | number)[], selectedLabels: string[]) => {
      if (!selectedDimension) return;

      // For age/generations, expand generation keys into age_interval IDs
      let finalValues: (string | number)[] = selectedValues;
      if (selectedDimension.key === 'age') {
        finalValues = selectedValues.flatMap(genKey => {
          const gen = GENERATION_OPTIONS.find(g => g.key === genKey);
          return gen ? [...gen.ids] : [];
        });
      }

      // Resolve platform info for channel dimensions (searchResults carry platform)
      const valuePlatforms: (string | null)[] | undefined =
        selectedDimension.key === 'channel'
          ? selectedValues.map(val => {
              const match = searchResults.find(r => r.value === val);
              return match?.platform ?? null;
            })
          : undefined;

      const newLayer: FunnelLayer = {
        id: generateId(),
        type: selectedDimension.type,
        dimension: selectedDimension.key,
        dimensionLabel: selectedDimension.label,
        values: finalValues,
        valueLabels: selectedLabels,
        ...(valuePlatforms ? { valuePlatforms } : {}),
      };

      // Track this layer ID as the last added (for animation control)
      lastAddedLayerIdRef.current = newLayer.id;

      if (activeFunnel === 'A') {
        setLayers(prev => [...prev, newLayer]);
      } else {
        setLayersB(prev => [...prev, newLayer]);
      }

      setIsValueModalOpen(false);
      setSelectedDimension(null);
    },
    [selectedDimension, activeFunnel]
  );

  const handleRemoveLayer = useCallback((layerId: string, funnel: 'A' | 'B') => {
    // Clear last added ref (removal means no animation needed)
    lastAddedLayerIdRef.current = null;

    if (funnel === 'A') {
      setLayers(prev => prev.filter(l => l.id !== layerId));
    } else {
      setLayersB(prev => prev.filter(l => l.id !== layerId));
    }
  }, []);

  // Derived stats (moved before handleSavePersona to fix initialization order)
  // Use calculation result if available, otherwise fall back to pre-loaded origin count
  const originCount = funnelA.results?.origin.count ?? preloadedOrigin?.count ?? 0;
  const originUnit =
    funnelA.results?.origin.unit ?? (preloadedOrigin?.unit as UnitType) ?? 'profiles';
  const finalCount = funnelA.results?.finalCount ?? originCount;
  const finalUnit = funnelA.results?.finalUnit ?? originUnit;
  const percentOfTotal = originCount > 0 ? ((finalCount / originCount) * 100).toFixed(2) : '0';
  const contentFilters = layers.filter(l => l.type === 'content').length;
  const audienceFilters = layers.filter(l => l.type === 'audience').length;

  const handleSavePersona = useCallback(
    async (name: string, description: string) => {
      try {
        await savePersona({
          name,
          description: description || undefined,
          layers: layers.map(l => ({
            type: l.type,
            dimension: l.dimension,
            values: [...l.values],
            dimensionLabel: l.dimensionLabel,
            valueLabels: [...l.valueLabels],
          })),
          finalCount,
          finalUnit,
          totalOrigin: originCount,
        });

        setIsSaveModalOpen(false);
        toast({
          title: 'Persona saved',
          description: `"${name}" saved successfully.`,
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to save persona. Please try again.',
          variant: 'destructive',
        });
      }
    },
    [layers, finalCount, finalUnit, originCount, savePersona, toast]
  );

  const handleOpenLoadModal = useCallback(() => {
    setIsLoadModalOpen(true);
    refetchPersonas();
  }, [refetchPersonas]);

  const handleSelectPersona = useCallback(
    (persona: Persona) => {
      // Convert persona layers back to FunnelLayer[] (add UUIDs)
      const restoredLayers: FunnelLayer[] = persona.layers.map(l => ({
        id: generateId(),
        type: l.type,
        dimension: l.dimension,
        dimensionLabel: l.dimensionLabel,
        values: [...l.values],
        valueLabels: [...l.valueLabels],
      }));

      // Replace current layers (Funnel A by default, or active funnel in compare mode)
      if (compareMode && activeFunnel === 'B') {
        setLayersB(restoredLayers);
      } else {
        setLayers(restoredLayers);
      }

      setIsLoadModalOpen(false);
      toast({
        title: 'Persona loaded',
        description: `"${persona.name}" loaded with ${persona.layerCount} ${persona.layerCount === 1 ? 'layer' : 'layers'}.`,
      });
    },
    [compareMode, activeFunnel, toast]
  );

  const handleDeletePersona = useCallback(
    async (id: number) => {
      await deletePersona(id);
      toast({
        title: 'Persona deleted',
        description: 'Persona removed successfully.',
      });
    },
    [deletePersona, toast]
  );

  const handleExploreAudience = useCallback(() => {
    const layerInputs = layers.map(l => ({
      type: l.type,
      dimension: l.dimension,
      values: [...l.values],
      dimensionLabel: l.dimensionLabel,
      valueLabels: [...l.valueLabels],
    }));
    const params = new URLSearchParams();
    params.set('layers', JSON.stringify(layerInputs));
    params.set('originCount', String(originCount));
    window.open(`/v11/funnel/explore?${params.toString()}`, '_blank');
  }, [layers, originCount]);

  // (Dimension counts are fetched on-demand when the picker modal opens)

  // Funnel B stats
  const originCountB = funnelB.results?.origin.count ?? originCount;
  const finalCountB = funnelB.results?.finalCount ?? originCountB;
  const finalUnitB = funnelB.results?.finalUnit ?? 'profiles';
  const percentOfTotalB = originCountB > 0 ? ((finalCountB / originCountB) * 100).toFixed(2) : '0';
  const contentFiltersB = layersB.filter(l => l.type === 'content').length;
  const audienceFiltersB = layersB.filter(l => l.type === 'audience').length;

  const usedDimensions =
    activeFunnel === 'A' ? layers.map(l => l.dimension) : layersB.map(l => l.dimension);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderOrigin = (count: number, unit: string, isLoading: boolean) => (
    <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4 text-center">
      <div className="text-xs text-v11-text-muted mb-1">
        All {unit === 'posts' ? 'Posts' : 'Profiles'}
      </div>
      <div className="text-2xl font-bold text-v11-text-primary">
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin inline" />
        ) : count > 0 ? (
          <CountUp end={count} duration={0.5} separator="," preserveValue />
        ) : (
          <span className="text-v11-text-muted">--</span>
        )}
      </div>
    </div>
  );

  const renderLayers = (
    layerResults: readonly FunnelLayerResult[],
    funnel: 'A' | 'B',
    isCalc: boolean
  ) => (
    <div className="flex flex-col items-center space-y-3">
      {layerResults.map((layer, index) => {
        const width = 100 - index * 8;
        const isContent = layer.type === 'content';
        // Only animate if this is the layer that was just added
        const isNewlyAdded = layer.id === lastAddedLayerIdRef.current;

        return (
          <React.Fragment key={layer.id}>
            {/* Connector */}
            <motion.div
              initial={isNewlyAdded ? { opacity: 0, scale: 0.8 } : false}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center"
            >
              <ArrowDown className="h-4 w-4 text-v11-text-muted" />
              {(isCalc && isNewlyAdded) || layer.reductionPercent !== 0 ? (
                <Badge
                  variant="destructive"
                  className={cn(
                    'text-xs',
                    layer.reductionPercent > 0
                      ? 'bg-green-500/15 text-green-500 border-green-500/30'
                      : 'bg-red-500/15 text-red-500 border-red-500/30'
                  )}
                >
                  {isCalc && isNewlyAdded ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    `${layer.reductionPercent > 0 ? '+' : ''}${layer.reductionPercent.toFixed(1)}%`
                  )}
                </Badge>
              ) : null}
            </motion.div>

            {/* Layer */}
            <motion.div
              initial={isNewlyAdded ? { opacity: 0, y: 20 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              style={{ width: `${width}%` }}
              className="w-full"
            >
              <div
                className={cn(
                  'rounded-lg p-4 transition-all group',
                  isContent
                    ? 'bg-cyan-500/10 border border-cyan-500/20 hover:border-cyan-500/40'
                    : 'bg-purple-500/10 border border-purple-500/20 hover:border-purple-500/40'
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <Badge
                      variant="secondary"
                      className="mb-2 bg-v11-surface-hover text-v11-text-muted text-xs"
                    >
                      {isContent ? (
                        <>
                          <FileText className="h-3 w-3 mr-1 inline" />
                          Content
                        </>
                      ) : (
                        <>
                          <Users className="h-3 w-3 mr-1 inline" />
                          Audience
                        </>
                      )}
                    </Badge>
                    <div className="text-xs uppercase tracking-wide text-v11-text-muted mb-1 font-semibold">
                      {layer.dimensionLabel}
                    </div>
                    <div className="text-sm font-semibold text-v11-text-primary flex flex-wrap items-center gap-1.5">
                      {layer.valueLabels.map((label, i) => {
                        // For Platform dimension, label IS the platform name
                        // For Channel dimension, use stored valuePlatforms
                        const platformKey =
                          layer.dimension === 'platform'
                            ? label
                            : (layer.valuePlatforms?.[i] ?? null);
                        const platformConfig = getPlatformIcon(platformKey);

                        return (
                          <span key={i} className="inline-flex items-center gap-1">
                            {platformConfig && (
                              <span style={{ color: platformConfig.color }} className="shrink-0">
                                {platformConfig.icon}
                              </span>
                            )}
                            <span>{label}</span>
                            {i < layer.valueLabels.length - 1 && (
                              <span className="text-v11-text-muted">,</span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-xl font-bold text-v11-text-primary tabular-nums">
                      {isCalc && isNewlyAdded ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : isNewlyAdded ? (
                        // Only animate the newly added layer
                        <CountUp
                          start={layer.previousCount}
                          end={layer.count}
                          duration={0.5}
                          separator=","
                          preserveValue
                        />
                      ) : (
                        // Previous layers: static value, no animation
                        layer.count.toLocaleString()
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleRemoveLayer(layer.id, funnel)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </React.Fragment>
        );
      })}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Main Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
            <Filter className="h-6 w-6 text-indigo-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-v11-text-primary">Funnel Builder</h1>
            <p className="text-sm text-v11-text-muted">Build personas step-by-step</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 mr-2">
            <Switch
              id="funnel-include-own-channels"
              checked={includeOwnChannels}
              onCheckedChange={setIncludeOwnChannels}
              className="scale-75"
            />
            <span
              className="text-xs text-v11-text-secondary cursor-pointer select-none"
              onClick={() => setIncludeOwnChannels(!includeOwnChannels)}
            >
              Show Own Channels
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={handleOpenLoadModal}>
            <FolderOpen className="h-4 w-4" />
            Load Persona
          </Button>
          {compareMode && (
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4" />
              Export Comparison
            </Button>
          )}
          <Button
            variant={compareMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCompareMode(!compareMode)}
          >
            <Scale className="h-4 w-4" />
            {compareMode ? 'Comparing' : 'Compare'}
          </Button>
        </div>
      </div>

      {/* Funnel Visualization */}
      <div className={cn('mx-auto space-y-6', compareMode ? 'max-w-7xl' : 'max-w-4xl')}>
        {/* Intro */}
        {!compareMode && layers.length === 0 && (
          <div className="text-center mb-6">
            <p className="text-sm text-v11-text-muted">
              Add filters step-by-step to discover your target persona
            </p>
          </div>
        )}

        {/* Dual Funnels Grid */}
        <div className={cn('space-y-6', compareMode && 'grid grid-cols-2 gap-8 space-y-0')}>
          {/* Funnel A */}
          <div className="space-y-4">
            {compareMode && (
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-v11-text-primary">Funnel A</h3>
              </div>
            )}

            {/* Origin */}
            {renderOrigin(
              originCount,
              originUnit,
              layers.length > 0 ? funnelA.isCalculating : isLoadingOrigin
            )}

            {/* Funnel Steps */}
            {layersWithResults.length > 0 &&
              renderLayers(layersWithResults, 'A', funnelA.isCalculating)}

            {/* Error */}
            {funnelA.error && (
              <div className="text-center text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                Calculation failed. Please try again.
              </div>
            )}

            {/* Add Filter */}
            <div className="text-center">
              <Button
                variant="outline"
                size="sm"
                className="border-dashed border-indigo-500 text-indigo-500 hover:bg-indigo-500/10"
                onClick={() => handleAddFilter('A')}
              >
                <Plus className="h-3.5 w-3.5" />
                Add Filter
              </Button>
            </div>
          </div>

          {/* Funnel B (Compare Mode) */}
          {compareMode && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-v11-text-primary">Funnel B</h3>
              </div>

              {/* Origin B */}
              {renderOrigin(originCountB, finalUnitB, funnelB.isCalculating && layersB.length > 0)}

              {/* Steps Funnel B */}
              {layersBWithResults.length > 0 &&
                renderLayers(layersBWithResults, 'B', funnelB.isCalculating)}

              {/* Error B */}
              {funnelB.error && (
                <div className="text-center text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  Calculation failed. Please try again.
                </div>
              )}

              {/* Add Filter B */}
              <div className="text-center">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-dashed border-indigo-500 text-indigo-500 hover:bg-indigo-500/10"
                  onClick={() => handleAddFilter('B')}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Filter
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Summary - Single Mode */}
        {layers.length > 0 && !compareMode && (
          <div className="bg-v11-surface border border-v11-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-v11-text-muted mb-1">Persona Audience Size</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-v11-text-primary tabular-nums">
                    {funnelA.isCalculating ? (
                      <Loader2 className="h-5 w-5 animate-spin inline" />
                    ) : (
                      <CountUp end={finalCount} duration={0.5} separator="," preserveValue />
                    )}
                  </span>
                  <span className="text-sm text-v11-text-muted">{finalUnit}</span>
                  <span className="text-xs text-v11-text-muted">({percentOfTotal}%)</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-v11-text-muted">
                  {audienceFilters > 0 && (
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {audienceFilters} audience
                    </span>
                  )}
                  {contentFilters > 0 && (
                    <span className="flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      {contentFilters} content
                    </span>
                  )}
                  <span>
                    {layers.length} {layers.length === 1 ? 'step' : 'steps'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-v11-border text-v11-text-secondary hover:bg-v11-surface-hover"
                    onClick={() => setIsSaveModalOpen(true)}
                    disabled={layers.length === 0 || funnelA.isCalculating}
                  >
                    <Save className="h-4 w-4" />
                    Save Persona
                  </Button>
                  <Button
                    size="sm"
                    className="bg-indigo-500 hover:bg-indigo-600"
                    onClick={handleExploreAudience}
                    disabled={layers.length === 0 || funnelA.isCalculating}
                  >
                    <Compass className="h-4 w-4" />
                    Explore Audience
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary - Compare Mode */}
        {compareMode && (layers.length > 0 || layersB.length > 0) && (
          <div className="bg-v11-surface border border-v11-border rounded-lg p-4">
            <div className="text-xs text-v11-text-muted mb-3">Persona Audience Size</div>

            <div className="grid grid-cols-2 gap-6">
              {/* Funnel A Stats */}
              <div className="border-l-2 border-indigo-500 pl-3">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xl font-bold text-v11-text-primary tabular-nums">
                    <CountUp end={finalCount} duration={0.5} separator="," preserveValue />
                  </span>
                  <span className="text-xs text-v11-text-muted">
                    {finalUnit} ({percentOfTotal}%)
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-v11-text-muted">
                  <span>
                    <FileText className="h-3 w-3 inline" /> {contentFilters}
                  </span>
                  <span>
                    <Users className="h-3 w-3 inline" /> {audienceFilters}
                  </span>
                  <span>{layers.length} steps</span>
                </div>
              </div>

              {/* Funnel B Stats */}
              <div className="border-l-2 border-indigo-500 pl-3">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xl font-bold text-v11-text-primary tabular-nums">
                    <CountUp end={finalCountB} duration={0.5} separator="," preserveValue />
                  </span>
                  <span className="text-xs text-v11-text-muted">
                    {finalUnitB} ({percentOfTotalB}%)
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-v11-text-muted">
                  <span>
                    <FileText className="h-3 w-3 inline" /> {contentFiltersB}
                  </span>
                  <span>
                    <Users className="h-3 w-3 inline" /> {audienceFiltersB}
                  </span>
                  <span>{layersB.length} steps</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <DimensionPickerModal
        open={isDimensionModalOpen}
        onClose={() => setIsDimensionModalOpen(false)}
        onSelectDimension={handleSelectDimension}
        usedDimensions={usedDimensions}
        onRequestCounts={handleRequestDimensionCounts}
      />

      <ValuePickerModal
        open={isValueModalOpen}
        onClose={() => {
          setIsValueModalOpen(false);
          setSelectedDimension(null);
          setSearchResults([]);
          setValueCounts(undefined);
        }}
        dimensionLabel={selectedDimension?.label ?? ''}
        values={dimensionValues}
        isLoading={isLoadingOptions && !isSearchDimension}
        searchMode={isSearchDimension}
        onSearch={isSearchDimension ? handleSearch : undefined}
        isSearching={isSearching}
        onApply={handleApplyValues}
        valueCounts={valueCounts}
        isLoadingCounts={isLoadingValueCounts}
      />

      <SavePersonaModal
        open={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSave={handleSavePersona}
        isSaving={isSaving}
        layerCount={layers.length}
        finalCount={finalCount}
        finalUnit={finalUnit}
      />

      <LoadPersonaModal
        open={isLoadModalOpen}
        onClose={() => setIsLoadModalOpen(false)}
        onSelect={handleSelectPersona}
        personas={personas}
        isLoading={isLoadingPersonas}
        onDelete={handleDeletePersona}
        isDeleting={isDeleting}
      />
    </div>
  );
};
